import { type LockRunner, webLockApiRunner } from '../internal/utils/lock-runner';
import { version } from '../version';
import { type ContextObject, type IdentifiersObject, type Json } from './event';
import { type EventProcessor } from './event-processors';

export type RequiredConfiguration = {
  /**
   * Authentication token for API requests, a property of a Source found in PicoUI.
   */
  identificationToken: string;
};

export type OptionalConfiguration<
  Context extends ContextObject = Record<string, Json>,
  Identifiers extends IdentifiersObject = Record<string, string | null>,
> = {
  /**
   * Closure to retrieve additional Context Values to attach to events.
   * Each source can have a custom context, which will be merged with the base context.
   * These values are still validated by the backend, so remember to inform PicoX
   * about their content by modifying custom context of the Source in the PicoX.
   *
   * For more information about context, please refer to the PicoX documentation.
   * (https://github.com/BendingSpoons/picox/blob/master/docs/user_guides/sources.md)
   */
  additionalContext: () => Context;

  /**
   * Closure to retrieve additional User Identifiers to attach to events.
   * Similar to the additionalContext, each source can have custom identifiers,
   * which will be merged with the base identifiers.
   * These identifiers are still validated by the backend, so remember to inform PicoX
   * about their content by modifying custom identifiers of the Source in the PicoX.
   *
   * For more information about context, please refer to the PicoX documentation.
   * (https://github.com/BendingSpoons/picox/blob/master/docs/user_guides/sources.md)
   */
  additionalIdentifiers: () => Identifiers;

  /**
   * The domain to associate the user identifier cookie with.
   * Best practice is to set this to your website's root domain (e.g., example.com) rather than a specific subdomain.
   *
   * Setting it to the same domain as your website ensures the cookie persists between sessions across all major browsers.
   * Many browsers have privacy protections that limit or clear third-party cookies, which can cause user identifiers to reset frequently.
   * If your application spans multiple subdomains, you can use a leading dot (e.g., .example.com)
   * to make the cookie available across all subdomains.
   */
  cookieDomain: string;

  /**
   * The endpoint to call for the PicoX API.
   */
  endpoint: string;

  /**
   * The name of the indexedDb database.
   * Default: "picox"
   */
  dbName: string;

  /**
   * Additional event processors to be used.
   */
  eventProcessors: EventProcessor[];

  /**
   * The size of the batch to send to the server.
   */
  eventsBatchSize: number;

  /**
   * Whether to use the development mode.
   * Development events will be accepted by the server and forwarded separately from production events.
   *
   * See the PicoX documentation for more information.
   * (https://github.com/BendingSpoons/picox/blob/master/docs/user_guides/development.md)
   */
  isDevelopment: boolean;

  /**
   * The logger to be used.
   */
  logger: Logger;

  /**
   * Maximum interval for exponential backoff in milliseconds.
   */
  maxExponentialBackoffInterval: number;

  /**
   * The maximum number of events that can be stored in the indexedDb database.
   */
  maxEventsStored: number;

  /**
   * This field has gained importance for Data Science.
   * We don't want the client app to choose the pico version, so we are enforcing it in the library.
   * Data science will use this value to discriminate between the old pico and the new one.
   */
  sdkVersion: string;

  /**
   * Base interval for exponential backoff in milliseconds.
   */
  retryBaseInterval: number;

  /**
   * The interval in milliseconds between sync operations.
   */
  syncIntervalMilliseconds: number;

  /**
   * The target size in events number of the indexedDb database after clearing space.
   */
  targetEventsNumberAfterClearingSpace: number;

  /**
   * The web lock api runner.
   */
  webLockApiRunner: LockRunner;

  /**
   * The suffix to be used for the generated local identifiers.
   * Most of the time you don't need to set this value as it will be generated automatically.
   *
   * However, in some cases, you may want to have the identifier logic to pick previously generated identifiers.
   * For instance, you may want to pick the identifiers generated from Pico2. In this case, you can properly customize
   * this variable to achieve your goal.
   */
  identifiersSuffix: string;
};

export type PartialPicoXConfiguration<
  C extends ContextObject = Record<string, Json>,
  I extends IdentifiersObject = Record<string, string | null>,
> = RequiredConfiguration & Partial<OptionalConfiguration<C, I>>;

export type PicoXConfiguration<
  C extends ContextObject = Record<string, Json>,
  I extends IdentifiersObject = Record<string, string | null>,
> = RequiredConfiguration & OptionalConfiguration<C, I>;

/**
 * Simple interface for a logger. Used to allow users of the library to inject whichever logger they want.
 */
export type Logger = {
  trace: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  debug: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
};

const defaultPicoXConfiguration: OptionalConfiguration = {
  additionalContext: () => ({}),
  additionalIdentifiers: () => ({}),
  cookieDomain: 'https://api.picox.bendingspoons.com',
  endpoint: 'https://api.picox.bendingspoons.com/v1/events',
  dbName: 'picox',
  eventsBatchSize: 100,
  eventProcessors: [],
  isDevelopment: false,
  logger: {
    //eslint-disable-next-line @typescript-eslint/no-empty-function
    debug: () => {},
    //eslint-disable-next-line @typescript-eslint/no-empty-function
    error: () => {},
    //eslint-disable-next-line @typescript-eslint/no-empty-function
    info: () => {},
    //eslint-disable-next-line @typescript-eslint/no-empty-function
    trace: () => {},
    //eslint-disable-next-line @typescript-eslint/no-empty-function
    warn: () => {},
  },
  maxEventsStored: 10_000,
  maxExponentialBackoffInterval: 5 * 60 * 1000, // 5 minutes
  sdkVersion: `web@${version}`,
  retryBaseInterval: 2 * 1000, // 2 seconds
  syncIntervalMilliseconds: 2 * 1000, // 2 seconds
  targetEventsNumberAfterClearingSpace: 1000,
  webLockApiRunner: webLockApiRunner(),
  identifiersSuffix: 'PICOX_ID',
};

export const buildPicoXConfiguration = <
  Context extends ContextObject = Record<string, Json>,
  Identifiers extends IdentifiersObject = Record<string, string | null>,
>(
  configuration: PartialPicoXConfiguration<Context, Identifiers>,
): PicoXConfiguration<Context, Identifiers> => {
  return {
    ...(defaultPicoXConfiguration as unknown as OptionalConfiguration<Context, Identifiers>),
    ...configuration,
  };
};
